@if (displayLabel()) {
	<label class="form-label">{{ 'general.vehicles' | translate }}</label>
}

<div
	uiDropdown
	[fullWidth]="fullDropdownWidth()"
	[ignoreTrigger]="searchOpen()"
	(openChange)="onDropdownStateChange($event)"
	data-cy="c-vehicles-select"
>
	<div
		uiDropdownTrigger
		class="flex gap-0.5 w-full"
		[uiDropdownDisabled]="isDisabled()"
		data-cy="c-vehicles-select-dropdown-trigger"
	>
		@if (searchOpen()) {
			<ui-input
				type="search"
				class="w-full h-10 z-select-search"
				[value]="searchText()"
				(valueChanged)="searchText.set($event)"
				[placeholder]="'general.search' | translate"
				#searchInput
				(keydown.arrowdown)="onArrowDownKeydown($event)"
				(keydown.tab)="onArrowDownKeydown($event)"
				(keydown.escape)="onEscapeKeydown($event)"
				(keydown.space)="$event.stopPropagation()"
				(keydown.enter)="$event.stopPropagation()"
			>
				@let overrideStyles = ' rounded-none! h-[38px] overflow-hidden';
				<ui-button
					icon="clock_backward"
					custom-icon
					type="link-dark"
					iconSize="s"
					class="h-[38px] mr-2"
					[styleClass]="(recentSearchesOpen() ? 'bg-primary-200!' : '') + overrideStyles"
					(buttonClicked)="onToggleRecentSearches()"
					[hidden]="recentSearches().length < 1"
					[uiTooltip]="'vehicle_select.recent_searches' | translate"
				></ui-button>
			</ui-input>
		} @else {
			<div
				class="flex rounded bg-standard cursor-pointer h-10 w-full border border-secondary-300 py-px px-3 items-center justify-between outline-hidden"
				[ngClass]="{
					'bg-secondary-100! color-secondary-400 cursor-not-allowed!': isDisabled(),
					'input-focused': focused() && !isDisabled(),
				}"
				[tabindex]="isDisabled() ? -1 : 0"
				(keydown.enter)="toggleDropdown()"
				(keydown.space)="toggleDropdown()"
				(focus)="onTriggerFocus()"
				(blur)="onTriggerBlur()"
				role="combobox"
				[attr.aria-expanded]="dropdownOpen()"
				[attr.aria-haspopup]="'listbox'"
				[attr.aria-disabled]="isDisabled()"
			>
				@if (value()?.length) {
					<div
						class="flex justify-between w-full items-center gap-2"
						[class.text-secondary-400]="isDisabled()"
					>
						<span class="line-clamp-1">
							{{ value()[0].name }} ({{ value()[0].serial }}) |
							{{ value()[0].union }}
						</span>

						<div class="flex items-center">
							@if (value().length > 1) {
								<ui-pill
									[value]="'+' + (value().length - 1).toString()"
									class="mr-1"
									[disabled]="isDisabled()"
									type="secondaryFilled"
								></ui-pill>
							}
						</div>
					</div>
				} @else {
					<span class="text-secondary-400 line-clamp-1">
						{{ placeholder() | translate }}
					</span>
				}
				<ui-icon
					icon="x_arrow_down"
					class="transition block"
					[disabled]="isDisabled()"
					[ngClass]="dropdownOpen() ? 'rotate-180' : ''"
					size="m"
				></ui-icon>
			</div>
		}
	</div>

	@if (vehicleResponse$ | async | vehicleSelect; as groups) {
		<div
			*uiDropdownContent
			class="flex flex-col"
			(keydown)="onDropdownKeydown($event)"
			role="listbox"
			[attr.aria-label]="'general.vehicles' | translate"
			data-cy="vehicles-select-dropdown"
		>
			<div
				class="max-h-64"
				[ngClass]="loading() ? 'overflow-hidden' : 'overflow-y-auto'"
				#optionsList
				(scroll)="onScroll()"
			>
				@if (showRecentSearches()) {
					<div>
						@for (search of recentSearches(); track search; let idx = $index) {
							<div
								class="flex items-center px-3 py-2 hover:bg-primary-100 cursor-pointer rounded transition focus:bg-primary-100 focus:outline-hidden"
								[tabindex]="0"
								role="button"
								[attr.aria-label]="'vehicle_select.search_for' | translate: { term: search }"
								[attr.data-recent-search-index]="idx"
								(click)="onRecentSearchClick(search)"
								(keydown.enter)="onRecentSearchClick(search); $event.preventDefault()"
								(keydown.space)="onRecentSearchClick(search); $event.preventDefault()"
								(keydown.arrowdown)="onArrowDownKeydown($event, $event.target)"
								(keydown.arrowup)="onArrowUpKeydown($event, $event.target)"
								(keydown.escape)="onEscapeKeydown($event)"
								(keydown.tab)="onTabKeydown($event, 'first')"
							>
								<ui-icon icon="clock_backward" class="mr-2" size="m"></ui-icon>
								<span class="text-secondary-800">{{ search }}</span>
							</div>
						}
					</div>
				}
				@if (!showRecentSearches() && groups.length === 0 && !loading()) {
					<div class="p-4 pb-2">
						<ui-notify [text]="'general.no_data' | translate"></ui-notify>
					</div>
				}
				@if (!showRecentSearches() && !loading()) {
					@for (group of groups; track group.uuid; let groupIndex = $index) {
						@let unionIsSelected = isUnionSelected(group);
						@if (unionIsSelected) {
							<union-select-option
								[union]="group.name"
								[options]="group.options"
								[selectedVehicles]="selectedVehicles() ?? []"
								(deselectAll)="onDeselectAllInUnion(group.uuid)"
								(selectAll)="onSelectAllInUnion(group.uuid)"
								(toggled)="onToggleUnion(group)"
								[toggleable]="true"
								[collapsed]="group.collapsed ?? false"
								[tabindex]="0"
								[attr.data-option-index]="'union-' + groupIndex"
								(keydown.enter)="onToggleUnionKeydown($event, group)"
								(keydown.space)="onToggleUnionKeydown($event, group)"
								(keydown.arrowdown)="onArrowDownKeydown($event, $event.target)"
								(keydown.arrowup)="onArrowUpKeydown($event, $event.target)"
								(keydown.escape)="onEscapeKeydown($event)"
								(keydown.tab)="onTabKeydown($event, 'first')"
								class="focus:bg-primary-100 focus:outline-hidden block"
								role="option"
								[attr.aria-selected]="unionIsSelected"
							>
							</union-select-option>
						}
						@if (!group.collapsed) {
							@for (vehicle of group.options; track vehicle.value; let vehicleIndex = $index) {
								<vehicle-select-option
									[option]="vehicle"
									[selected]="isVehicleSelected(vehicle.value)"
									[unionSelected]="unionIsSelected"
									(toggleItem)="onSelect($event)"
									(selectWholeUnion)="onSelectAllInUnion(group.uuid)"
									[role]="role()"
									[tabindex]="0"
									[displayVehicleModel]="displayVehicleModel()"
									[attr.data-option-index]="'vehicle-' + groupIndex + '-' + vehicleIndex"
									(keydown.enter)="onSelectVehicleKeydown($event, vehicle.value)"
									(keydown.space)="onSelectVehicleKeydown($event, vehicle.value)"
									(keydown.arrowdown)="onArrowDownKeydown($event, $event.target)"
									(keydown.arrowup)="onArrowUpKeydown($event, $event.target)"
									(keydown.escape)="onEscapeKeydown($event)"
									(keydown.tab)="onTabKeydown($event, 'first')"
									class="focus:bg-primary-100 focus:outline-hidden block"
									[attr.role]="'option'"
									[attr.aria-selected]="isVehicleSelected(vehicle.value)"
								></vehicle-select-option>
							}
						}
					}
				}

				@if (loading()) {
					<div class="w-full h-10 mt-1">
						<ui-spinner [inline]="true" [size]="'s'"></ui-spinner>
					</div>
				}
			</div>
			@if (selectedVehicles().length > maxSelections()) {
				<div class="pl-3 pr-3 absolute bottom-10 w-full">
					<ui-notify
						[text]="maxSelectedVehiclesText() | translate: { max: maxSelections() }"
						type="warning"
					></ui-notify>
				</div>
			}

			<div
				class="flex items-center border-t border-secondary-300 h-10 w-full justify-center"
				[hidden]="loading() || recentSearchesOpen()"
			>
				@if (selectedVehicles().length > 0) {
					<div
						class="flex items-center text-secondary-800 cursor-pointer text-sm p-2 user-select-none justify-center w-full focus:outline-secondary-800 focus:outline-1"
						[ngClass]="{
							'text-secondary-400! cursor-not-allowed!': loading(),
						}"
						(click)="onDeselectAll()"
						(keydown.enter)="onDeselectAllKeydown($event)"
						(keydown.space)="onDeselectAllKeydown($event)"
						(keydown.arrowdown)="onArrowDownKeydown($event, $event.target)"
						(keydown.arrowup)="onArrowUpKeydown($event, $event.target)"
						(keydown.escape)="onEscapeKeydown($event)"
						(keydown.tab)="onTabKeydown($event, 'second')"
						data-cy="c-vehicle-union-select-button-deselect-all"
						[tabindex]="0"
						role="button"
						[attr.aria-label]="'select.deselect_all' | translate"
					>
						{{ 'select.deselect_all' | translate }}
					</div>
				}
				<div
					class="flex items-center h-full text-sm p-2 user-select-none justify-center w-full focus:outline-secondary-800 focus:outline-1"
					[class]="
						isDisabledStyle()
							? 'text-secondary-400 cursor-not-allowed'
							: 'bg-primary-700 text-white dark:text-secondary-800 hover:bg-primary-900 cursor-pointer'
					"
					(click)="onSave()"
					(keydown.enter)="onSaveKeydown($event)"
					(keydown.space)="onSaveKeydown($event)"
					(keydown.arrowup)="onArrowUpKeydown($event, $event.target)"
					(keydown.escape)="onEscapeKeydown($event)"
					data-cy="c-vehicle-union-select-button-save"
					[tabindex]="0"
					role="button"
					[attr.aria-label]="'general.save' | translate"
				>
					{{ 'general.save' | translate }}

					<span class="ml-1">({{ selectedVehicles().length }})</span>
				</div>
			</div>
		</div>
	}
</div>
